import { gettext } from '@eventbrite/i18n';
import { ChoiceCard } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import React, { useState } from 'react';
import { LocalizedDateTime, SeriesDatesScopedProps } from '../../../../types';
import { getFormattedDateCard } from '../../../utils/localizedDates';
import { DetailInner } from '../../../_shared/Detail/Detail';
import './ChildEventsSlider.scss';

type ChildEventCardProps = {
    url: string;
    display: {
        weekDay: string;
        fullWeekDay: string;
        time: string;
        month: string;
        dayOfMonth: string;
        year: string;
    };
    isCurrentListing: boolean;
};

const CHILD_EVENTS_THRESHOLD = 5;

// Noop function to give to the ChoiceCard card component the behavior of a clickable element
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => undefined;

type MoreOptions = {
    onButtonClick: () => void;
};

const MORE_OPTIONS = gettext('More options');
const MoreOptionsCard = ({ onButtonClick }: MoreOptions) => (
    <ChoiceCard onClick={noop}>
        <button
            className="child-event-date-card__button"
            onClick={onButtonClick}
            data-testid="more-options-button-card"
        >
            {MORE_OPTIONS}
        </button>
    </ChoiceCard>
);

const ChildEventItem = ({
    isCurrentListing,
    url,
    display,
}: ChildEventCardProps) => {
    const { fullWeekDay, weekDay, time, month, dayOfMonth, year } = display;
    const formattedMonth =
        new Date().getFullYear().toString() === year
            ? `${month}`
            : `${month} ${year}`;
    return (
        <li className="child-event-date-card">
            <ChoiceCard
                className="child-event-date-card-inner"
                selected={isCurrentListing}
                onClick={noop}
            >
                <a
                    className={`child-event-date-card-link
                        ${
                            isCurrentListing &&
                            'child-event-date-card--selected'
                        }`}
                    href={url}
                >
                    <abbr
                        title={fullWeekDay}
                        className="child-event-date-card__week-day"
                    >
                        {fullWeekDay.length <= 9 ? fullWeekDay : weekDay}
                    </abbr>
                    <hr />
                    <abbr
                        title={formattedMonth}
                        className="child-event-date-card__month"
                    >
                        {' '}
                        {formattedMonth}
                    </abbr>
                    <span className="child-event-date-card__day">
                        {' '}
                        {dayOfMonth}
                    </span>
                    <span className="child-event-date-card__time"> {time}</span>
                </a>
            </ChoiceCard>
        </li>
    );
};

const filterEventDates = ({ childEvents, eventId }: SeriesDatesScopedProps) => {
    const eventDates = [];
    const slicedEventDates = childEvents.slice(0, CHILD_EVENTS_THRESHOLD);

    for (let i = 0; i < slicedEventDates.length; i++) {
        const item = childEvents[i];

        eventDates.push({
            value: item.id,
            url: item.url,
            display: getFormattedDateCard({
                date: item.start,
                locale: item.locale,
            }),
            isCurrentListing: item.id === eventId,
        });
    }
    return eventDates;
};

const LazyMoreOptionsModal = loadable(
    () => import('./MoreOptionsModal/MoreOptionsModal'),
);

export type ChildEventsSliderProps = SeriesDatesScopedProps & {
    currentEventStartDate: LocalizedDateTime;
    currentEventId: string;
};

export const ChildEventsSlider = (props: ChildEventsSliderProps) => {
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);
    const { childEvents, eventId } = props;

    const filteredEventDates = filterEventDates({
        childEvents,
        eventId,
    });

    const childEventCards = filteredEventDates.map(
        ({ value, display, url, isCurrentListing }) => {
            return (
                <ChildEventItem
                    key={value}
                    url={url}
                    display={display}
                    isCurrentListing={isCurrentListing}
                />
            );
        },
    );

    const handleMoreOptionsClick = () => {
        setMoreOptionsModalOpened(true);
    };

    const shouldRenderMoreOptionsButton =
        childEvents.length > CHILD_EVENTS_THRESHOLD;

    return (
        <>
            <div className="child-event-dates-wrapper">
                <DetailInner>
                    <div className="child-event-dates">
                        <ul
                            className="child-event-dates-list"
                            data-testid="child-events-slider"
                        >
                            {childEventCards}
                            {shouldRenderMoreOptionsButton && (
                                <li className="child-event-date-card">
                                    <MoreOptionsCard
                                        onButtonClick={handleMoreOptionsClick}
                                    />
                                </li>
                            )}
                        </ul>
                    </div>
                </DetailInner>
            </div>
            {moreOptionsModalOpened && (
                <LazyMoreOptionsModal
                    onClose={() => setMoreOptionsModalOpened(false)}
                    opened={moreOptionsModalOpened}
                    childEvents={childEvents}
                    currentEventStartDate={props.currentEventStartDate}
                    currentEventId={props.currentEventId}
                />
            )}
        </>
    );
};
